import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, UserRound, Wallet } from 'lucide-react';
import { Badge, Button, Card, CardContent, Input } from '@mlm/ui';
import { Decimal } from '@mlm/shared';
import {
  approveTopup,
  createTopup,
  getPublicTenantConfig,
  getTopupConfig,
  listMemberActiveSlots,
  listTopupMethods,
  previewTopup,
  type MemberSearchResult,
  type PosPaymentMethodOption,
  type TopupPreview,
} from '../lib/api';
import { isTopupAmount, TOPUP_PRESETS, topupChange } from '../lib/topup';
import { useTopupStore } from '../lib/topup-store';
import { useActiveBranch } from '../lib/use-branch';
import { BranchPicker } from '../components/branch-picker';
import { CustomerSheet } from '../components/pos/customer-sheet';
import { ManagerPinForm } from '../components/pos/manager-pin-form';
import { PaymentGrid } from '../components/pos/payment-grid';
import { SlotBalances } from '../components/pos/slot-balances';
import { SlotPickerDialog } from '../components/pos/slot-picker-dialog';

const peso = (v: string | number) =>
  `₱${Number(v).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ERROR_COPY: Record<string, string> = {
  pos_topup_disabled: 'Top-ups at the till are switched off.',
  branch_scope_violation: 'You can only top up at your own branch.',
  topup_not_at_stockist: 'A stockist branch cannot take wallet top-ups.',
  unknown_payment_method: 'That payment method is not available to you.',
  topup_method_not_allowed: 'That payment method cannot pay a top-up here.',
  topup_reference_required: 'Enter the payment reference (card slip or e-wallet number).',
  slot_not_active: 'That slot is not active.',
  slot_not_yours: 'That slot belongs to someone else.',
  duplicate_reference: 'That reference was already used for a top-up.',
  invalid_amount: 'Enter an amount above zero.',
  topup_needs_manager_pin: "A top-up this large needs a manager's PIN.",
};

/** No answer came back, so nobody knows whether it went through. */
const UNCERTAIN = 'uncertain';

/**
 * Wallet top-up at the till (cashier phase 5). The cashier loads a member's
 * CASH wallet, paid in cash (it goes in the drawer, and shift close expects it)
 * or, when the tenant allows it, by card or e-wallet with a reference. It is
 * approved on the spot and earns the same bracket bonus and gift checks as an
 * approved top-up request.
 *
 * The form, its idempotency key and the in-flight flag live in
 * `lib/topup-store.ts`, so leaving the page and coming back keeps them. The key
 * is renewed only by starting a new top-up: retrying after a lost answer
 * replays the first one instead of loading twice, and a CHANGED request under a
 * key that already loaded is refused by the server (`idempotency_key_conflict`),
 * which this page turns into "check the wallet, then start over".
 */
export function TopupPage() {
  const qc = useQueryClient();
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const configQ = useQuery({
    queryKey: ['public-tenant-config'],
    queryFn: getPublicTenantConfig,
    staleTime: 60_000,
  });
  const enabled = configQ.data?.posTopupEnabled === true;
  const label = configQ.data?.voucherLabel ?? 'Gift Check';
  const methodsQ = useQuery({
    queryKey: ['topup-methods'],
    queryFn: listTopupMethods,
    enabled,
  });
  const topupConfigQ = useQuery({
    queryKey: ['topup-config'],
    queryFn: getTopupConfig,
    enabled,
  });

  const draft = useTopupStore();
  const { member, slots, slot, amount, methodCode, reference, received, confirming, approvalChip } =
    draft;
  const [pickingMember, setPickingMember] = useState(false);
  const [pickingSlot, setPickingSlot] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const methods: PosPaymentMethodOption[] = (methodsQ.data ?? []).map((m) => ({
    ...m,
    currency: 'cash',
    fees: [],
  }));
  const method = methods.find((m) => m.code === methodCode) ?? methods[0] ?? null;
  const amountOk = isTopupAmount(amount);
  const cash = method?.tenderKind === 'cash';
  const exact = method?.tenderKind === 'exact';
  const change = cash ? topupChange(amount, received) : null;
  // A top-up of the tenant's PIN amount or more needs a manager's approval.
  const pinAbove = topupConfigQ.data?.pinAbove ?? null;
  const pinNeeded =
    pinAbove != null && amountOk && new Decimal(amount.trim()).gte(new Decimal(pinAbove));

  const previewQ = useQuery({
    queryKey: ['topup-preview', amount.trim()],
    queryFn: () => previewTopup(amount.trim()),
    enabled: enabled && amountOk,
    staleTime: 30_000,
  });

  /**
   * Any edit leaves the confirm step and clears an earlier refusal. A manager's
   * approval was given for one member, slot and amount, so changing any of
   * them drops it.
   */
  function edit(patch: Parameters<typeof draft.set>[0]) {
    const voids = 'member' in patch || 'slot' in patch || 'amount' in patch;
    draft.set({ ...patch, confirming: false, ...(voids ? { approvalChip: null } : {}) });
    setError(null);
  }

  async function pickMember(m: MemberSearchResult) {
    setPickingMember(false);
    edit({ member: m, slot: null, slots: [] });
    const list = await qc.fetchQuery({
      queryKey: ['member-active-slots', m.id],
      queryFn: () => listMemberActiveSlots(m.id),
    });
    // A slot code typed in the search binds that slot; one slot needs no choice.
    const bound = m.slotId ? list.find((s) => s.id === m.slotId) : undefined;
    draft.set({ slots: list, slot: bound ?? (list.length === 1 ? list[0]! : null) });
    if (!bound && list.length > 1) setPickingSlot(true);
  }

  function startOver() {
    draft.reset();
    setError(null);
  }

  async function charge() {
    const state = useTopupStore.getState();
    if (state.charging || !branchId || !state.member || !state.slot || !method) return;
    state.setCharging(true);
    setError(null);
    try {
      const res = await createTopup({
        branchId,
        userId: state.member.id,
        slotId: state.slot.id,
        amount: state.amount.trim(),
        paymentMethodCode: method.code,
        reference: method.tenderKind === 'exact' ? state.reference.trim() || null : null,
        idempotencyKey: state.key,
        approvalChip: state.approvalChip,
      });
      useTopupStore.getState().finish(res);
      void qc.invalidateQueries({ queryKey: ['member-active-slots', res.userId] });
    } catch (err) {
      const response = (err as { response?: { status?: number; data?: { error?: string } } })
        .response;
      useTopupStore.getState().setCharging(false);
      // No answer, or the server failed: it may or may not have gone through.
      setError(
        !response || (response.status ?? 500) >= 500
          ? UNCERTAIN
          : (response.data?.error ?? UNCERTAIN),
      );
    }
  }

  const short = change && 'short' in change ? change.short : null;
  // Everything but a manager's approval, which is given ON the confirm step:
  // holding "Top up" back until it exists left a large top-up with no way in.
  const canConfirm =
    !!branchId &&
    !!member &&
    !!slot &&
    amountOk &&
    !!method &&
    !short &&
    (!exact || reference.trim().length > 0) &&
    !draft.charging;
  const ready = canConfirm && (!pinNeeded || !!approvalChip);
  const memberName = member?.fullName ?? member?.email ?? '';

  if (configQ.isSuccess && !enabled) {
    return (
      <Card>
        <CardContent className="space-y-2 p-6 text-base">
          <p className="font-medium">Wallet top-up at the till is off.</p>
          <p className="text-muted-foreground">
            An administrator turns it on in Settings, Wallet top-up.
          </p>
          <Link to="/" className="text-primary underline">
            Back to the till
          </Link>
        </CardContent>
      </Card>
    );
  }

  const done = draft.done;
  if (done) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card>
          <CardContent className="space-y-4 p-6">
            <div className="no-print space-y-1">
              <h1 className="text-2xl font-semibold">Wallet loaded</h1>
              <p className="text-base text-muted-foreground">
                {peso(done.principal)} was credited to {done.memberName}&apos;s cash wallet
                {done.replayed
                  ? ' (already recorded on the first try; nothing was added twice)'
                  : ''}
                .
              </p>
            </div>
            {/* Everything on the slip comes from the server's answer. */}
            <div className="receipt-tape space-y-1 rounded-lg border p-4 font-mono text-sm">
              <div className="pb-2 text-center">
                <div className="text-base font-semibold">Wallet top-up</div>
                <div className="text-xs">{done.branchName}</div>
                <div className="text-xs">{new Date(done.createdAt).toLocaleString()}</div>
              </div>
              <TapeRow label="Member" value={done.memberName} />
              <TapeRow label="Slot" value={done.slotCode} />
              <TapeRow label="Paid by" value={done.methodLabel} />
              {done.reference && <TapeRow label="Reference" value={done.reference} />}
              <TapeRow label="Loaded" value={peso(done.principal)} bold />
              {done.bonuses.map((b, i) => (
                <TapeRow
                  key={i}
                  label={b.currency === 'gc' ? 'Bonus (GC)' : 'Bonus'}
                  value={peso(b.amount)}
                />
              ))}
              {done.vouchers.map((v) => (
                <TapeRow key={v.id} label={`${label}: ${v.programName}`} value={v.code} />
              ))}
            </div>
            <div className="no-print flex flex-wrap gap-3">
              <Button className="h-12 px-6" onClick={startOver}>
                New top-up
              </Button>
              <Button variant="outline" className="h-12 px-6" onClick={() => window.print()}>
                Print slip
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Wallet top-up</h1>
        <BranchPicker
          branches={branches}
          branchId={branchId}
          isScoped={isScoped}
          onChange={setSelectedBranchId}
        />
      </div>

      {!branchId && (
        <p className="rounded-lg border border-destructive/40 p-3 text-sm text-destructive">
          There is no branch to take the top-up at. You need a branch assigned to your account.
        </p>
      )}

      <Card>
        <CardContent className="space-y-3 p-5">
          <h2 className="text-lg font-semibold">Member</h2>
          {member ? (
            <div className="flex flex-wrap items-center gap-3">
              <UserRound className="h-6 w-6 text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium">{memberName}</p>
                <p className="text-sm text-muted-foreground">
                  {slot ? `Slot ${slot.slotCode} · ${slot.membershipName}` : 'No slot picked'}
                </p>
              </div>
              {slots.length > 1 && (
                <Button variant="outline" onClick={() => setPickingSlot(true)}>
                  Change slot
                </Button>
              )}
              <Button variant="outline" onClick={() => setPickingMember(true)}>
                Change member
              </Button>
            </div>
          ) : (
            <Button className="h-12 px-6" onClick={() => setPickingMember(true)}>
              Find member
            </Button>
          )}
          {member && slots.length === 0 && !slot && (
            <p className="text-sm text-destructive">
              This member has no active slot, so no wallet to load. A free slot can be activated
              from the till.
            </p>
          )}
          <SlotBalances slot={slot} showGc={false} showCoin={false} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-5">
          <h2 className="text-lg font-semibold">Amount</h2>
          <div className="flex h-[54px] items-center gap-2 rounded-[15px] bg-muted px-4">
            <span className="text-lg text-muted-foreground">₱</span>
            <input
              value={amount}
              onChange={(e) => edit({ amount: e.target.value })}
              inputMode="decimal"
              placeholder="0.00"
              aria-label="Top-up amount"
              className="h-full w-full min-w-0 bg-transparent text-xl outline-none"
            />
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {TOPUP_PRESETS.map((p) => (
              <Button
                key={p}
                variant="outline"
                className="h-12"
                onClick={() => edit({ amount: String(p) })}
              >
                {p.toLocaleString('en-PH')}
              </Button>
            ))}
          </div>
          {amountOk && previewQ.data && <Rewards preview={previewQ.data} label={label} />}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-5">
          <h2 className="text-lg font-semibold">Paid by</h2>
          {methods.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {methodsQ.isLoading ? 'Loading…' : 'No cash method is set up for the till.'}
            </p>
          ) : (
            <PaymentGrid
              methods={methods}
              selectedCode={method?.code ?? null}
              onSelect={(c) => edit({ methodCode: c })}
              isDisabled={() => false}
            />
          )}
          {exact && (
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground" htmlFor="topup-reference">
                Reference (card slip or e-wallet number)
              </label>
              <Input
                id="topup-reference"
                value={reference}
                maxLength={100}
                onChange={(e) => edit({ reference: e.target.value })}
              />
            </div>
          )}
          {cash && (
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground" htmlFor="topup-received">
                Cash received (optional, for the change)
              </label>
              <Input
                id="topup-received"
                inputMode="decimal"
                value={received}
                onChange={(e) => draft.set({ received: e.target.value })}
                placeholder="0.00"
              />
              {change && 'change' in change && (
                <p className="text-base font-medium">Change: {peso(change.change)}</p>
              )}
              {short && <p className="text-sm text-destructive">That is {peso(short)} short.</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {error === UNCERTAIN || error === 'idempotency_key_conflict' ? (
        <Card className="border-destructive">
          <CardContent className="space-y-3 p-5">
            <p className="text-base font-medium">
              {error === UNCERTAIN
                ? 'We could not confirm this top-up.'
                : 'An earlier attempt on this screen went through with different details.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {error === UNCERTAIN
                ? "It may have gone through. Check the member's wallet. To try again, press Top up with the same details: if it went through, it will not load twice."
                : 'Check the wallet of the member from that attempt before you load anything else, then start over.'}
            </p>
            <Button variant="outline" className="h-12 px-6" onClick={startOver}>
              Start over
            </Button>
          </CardContent>
        </Card>
      ) : (
        error && (
          <p className="text-sm text-destructive">{ERROR_COPY[error] ?? 'The top-up failed.'}</p>
        )
      )}

      {confirming && member && slot && method ? (
        <Card className="border-primary">
          <CardContent className="space-y-4 p-5">
            <p className="text-lg">
              Load <strong>{peso(amount)}</strong> into {memberName}&apos;s wallet (slot{' '}
              {slot.slotCode}), paid by {method.label}?
            </p>
            <p className="text-sm text-muted-foreground">
              It is credited at once and cannot be undone at the till.
            </p>
            {pinNeeded &&
              (approvalChip ? (
                <p className="text-sm font-medium text-success">Approved by a manager.</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    A top-up of {peso(pinAbove!)} or more needs a manager&apos;s PIN.
                  </p>
                  <ManagerPinForm
                    requestApproval={({ managerEmail, pin }) =>
                      approveTopup({ managerEmail, pin, slotId: slot.id, amount: amount.trim() })
                    }
                    deniedMessage="That user cannot approve top-ups."
                    onApproved={(chip) => draft.set({ approvalChip: chip })}
                    onCancel={() => draft.set({ confirming: false })}
                  />
                </div>
              ))}
            <div className="flex flex-wrap gap-3">
              <Button className="h-12 px-6" disabled={!ready} onClick={() => void charge()}>
                <Wallet className="mr-2 h-5 w-5" />
                {draft.charging ? 'Loading…' : `Confirm ${peso(amount)}`}
              </Button>
              <Button
                variant="outline"
                className="h-12 px-6"
                disabled={draft.charging}
                onClick={() => draft.set({ confirming: false })}
              >
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          className="h-12 w-full text-base"
          disabled={!canConfirm}
          onClick={() => draft.set({ confirming: true })}
        >
          {amountOk ? `Top up ${peso(amount)}` : 'Top up'}
        </Button>
      )}

      <CustomerSheet
        open={pickingMember}
        stockistMode={false}
        onPick={(m) => void pickMember(m)}
        onClose={() => setPickingMember(false)}
      />
      <SlotPickerDialog
        open={pickingSlot}
        slots={slots}
        memberLabel={memberName}
        showGc={false}
        showCoin={false}
        onPick={(s) => {
          edit({ slot: s });
          setPickingSlot(false);
        }}
        onClose={() => setPickingSlot(false)}
      />
    </div>
  );
}

function Rewards({ preview, label }: { preview: TopupPreview; label: string }) {
  if (preview.bonuses.length === 0 && preview.vouchers.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/60 p-3 text-sm">
      <Gift className="h-5 w-5 text-primary" aria-hidden />
      <span className="font-medium">This top-up also earns</span>
      {preview.bonuses.map((b, i) => (
        <Badge key={i} variant="secondary">
          +{peso(b.amount)}
          {b.currency === 'gc' ? ' GC' : ''} bonus
        </Badge>
      ))}
      {preview.vouchers.map((v) => (
        <Badge key={v.programName} variant="secondary">
          {label}: {v.programName}
        </Badge>
      ))}
    </div>
  );
}

function TapeRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between gap-3 ${bold ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <span className="text-right tabular-nums">{value}</span>
    </div>
  );
}
